import { Basic } from "../basic.js";
import styles from './combobox.css'

const template = () => `
    <style>
       ${styles}
    </style>
    <div class="combobox" tabindex="0"> 
        <div class="combobox__btn" tabindex="0"> 
            <span class="combobox__name"></span> 
            <div class="combobox__arrow">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 10.2654L4 6.26536L4.93333 5.33203L8 8.3987L11.0667 5.33203L12 6.26536L8 10.2654Z" fill="black"/>
                </svg>
            </div>
        </div> 
    </div> 
`;

const dependencies = [
    "fontname",
    "fontsize",
    "fontitalic",
    "fontbold",
    "fontunderlined",
    "textcolor",
    "texthorizontalalign",
    "itemssource",
    "selecteditem",
    "notsendbackwardconnection",
    "listroundingradius",
    "listbackgroundfill",
    "listframethickness",
    "listframecolor",
    "itemtextcolor",
    "itembghover",
    "itemtextcolorhover",
    "backgroundcolor",
    "cornerradius",
];

/**
 * @class MSComboBox
 * @extends Basic
 * @classdesc Выпадающий список
 * */
export class MSComboBox extends Basic {
    constructor() {
        super();
        this._lockLink =
            this.getAttribute("notsendbackwardconnection") !== "false";
        this.shRoot = this.attachShadow({
            mode: window._enums.Environment.test ? "open" : "closed"
        });

        this.shRoot.innerHTML = template();
        this.style.overflow = "visible";
        this.combobox = this.shRoot.querySelector(".combobox");
        this.comboboxBtn = this.combobox.querySelector(".combobox__btn");
        this.listName = this.comboboxBtn.querySelector(".combobox__name");
        this.listWrapper = document.createElement('div');
        this.listWrapper.className = 'combobox-list-wrapper';
        this.listContent = document.createElement('ul');
        this.listContent.className = 'combobox-list';
        this.listWrapper.appendChild(this.listContent)
        this.style.backgroundColor = 'rgb(255,255,255)';
        this.listName.style.fontSize = '12px';
    }

    static get observedAttributes() {
        return super.observedAttributes.concat(dependencies);
    }

    // Радиус скругления
    get cornerradius() {
        return this._cornerradius ?? 0;
    }

    set cornerradius(value) {
        this._cornerradius = value;
        this.comboboxBtn.style.borderRadius = `${value}px`;
        this.style.borderRadius = `${value}px`;
    }

    // Вид шрифта
    get fontname() {
        return this._textFontFamily;
    }
    set fontname(value) {
        this._textFontFamily = value;
        this.listContent.style.fontFamily = "" + value + "";
        this.comboboxBtn.style.fontFamily = "" + value + "";
    }

    // Размер шрифта
    get fontsize() {
        return this._textFontSize ?? 18;
    }
    set fontsize(value) {
        this._textFontSize = value;
        this.listName.style.fontSize = value;
        this.listContent.style.fontSize = value;
    }

    // Цвет текста заголовка
    get textcolor() {
        return this._textcolor || '#000';
    }
    set textcolor(value) {
        this._textcolor = value;
        value = this.tryToGetUserColor(value);
        this.listName.style.color = value;
        this.combobox.style.textDecorationColor = value;
    }

    // Курсив шрифта
    get fontitalic() {
        return this._italic ?? false;
    }
    set fontitalic(value) {
        this._italic = this._toBool(value);
        const fontStyle = this._italic ? "italic" : "normal";
        this.listContent.style.fontStyle = fontStyle;
        this.combobox.style.fontStyle = fontStyle;
    }
    // Жирный шрифт
    get fontbold() {
        return this._bold ?? false;
    }
    set fontbold(value) {
        this._bold = this._toBool(value);
        const fontWeight = this._bold ? "bold" : "normal";
        this.listContent.style.fontWeight = fontWeight;
        this.combobox.style.fontWeight = fontWeight;
    }

    // Подчеркивание
    get fontunderlined() {
        return this._fontunderlined ?? false;
    }
    set fontunderlined(value) {
        this._fontunderlined = this._toBool(value);
        const textDecoration = this._fontunderlined ? "underline" : "none";
        this.combobox.style.textDecoration = textDecoration;
        this.listContent.style.textDecoration = textDecoration;
        this.listContent.style.setProperty('--text-decoration-hover', textDecoration)
    }

    // Горизонтальное выравнивание
    get texthorizontalalign() {
        return this._horizontalalign;
    }
    set texthorizontalalign(value) {
        this._horizontalalign = +value;
        const setTextAlign = (element, alignValue) => {
            switch (alignValue) {
                case 2:
                    element.style.textAlign = 'end';
                    break;
                case 1:
                    element.style.textAlign = 'center';
                    break;
                default:
                    element.style.textAlign = 'left';
                    break;
            }
        };
        setTextAlign(this.listContent, this._horizontalalign)
        setTextAlign(this.listName, this._horizontalalign);
    }

    // Цвет текста элемента списка
    get itemtextcolor() {
        return this._itemtextcolor || '#000';
    }
    set itemtextcolor(value) {
        this._itemtextcolor = value;
        value = this.tryToGetUserColor(value);
        this.listContent.style.color = value;
        this.listContent.style.textDecorationColor = value;
    }

    // Заливка фона
    get backgroundcolor() {
        return this._backgroundcolor;
    }
    set backgroundcolor(value) {
        this._backgroundcolor = value;
        value = this.tryToGetUserColor(value);
        const isGradient = value.indexOf(" ") !== -1;
        this.style.background = isGradient ? this._calcGradient(value) : value;
    }

    // Цвет заливки заднего фона списка
    get listbackgroundfill() {
        return this._listbackgroundfill || '#FFFFFF';
    }
    set listbackgroundfill(value) {
        this._listbackgroundfill = value;
        this.listContent.style.backgroundColor = this.tryToGetUserColor(value);
    }

    // Радиус скругления выпадающего списка
    get listroundingradius() {
        return this._listroundingradius ?? 0;
    }
    set listroundingradius(value) {
        this._listroundingradius = value;
        this.listWrapper.style.borderRadius = `${value}px`;
    }

    // Толщина рамки списка
    get listframethickness() {
        return this._listframethickness ?? 1;
    }
    set listframethickness(value) {
        this._listframethickness = value;
        this.listWrapper.style.borderWidth = `${value}px`;
    }

    // Цвет рамки списка
    get listframecolor() {
        return this._listframecolor || '#808080';
    }
    set listframecolor(value) {
        this._listframecolor = value;
        this.listWrapper.style.borderColor = this.tryToGetUserColor(value);
    }

    // Заливка фона элемента при наведении
    get itembghover() {
        return this._itembghover || '#1F75CC';
    }
    set itembghover(value) {
        this._itembghover = value;
        this.listContent.style.setProperty('--item-bg-hover', this.tryToGetUserColor(value));
    }

    // Цвет текста элемента при наведении
    get itemtextcolorhover() {
        return this._itemtextcolorhover || '#FFFFFF';
    }
    set itemtextcolorhover(value) {
        this._itemtextcolorhover = value;
        value = this.tryToGetUserColor(value);
        this.listContent.style.setProperty('--item-text-color-hover', value);
        this.listContent.style.setProperty('--underline-color-hover', value);
    }

    // Вид курсора
    get mousecursorkind() {
        return this._mousecursorkind || 'default';
    }
    set mousecursorkind(value) {
        this._mousecursorkind = value;
        this.comboboxBtn.style.cursor = this.getCursor(value);
    }

    // Элементы списка
    get itemssource() {
        return this._itemssource ?? "";
    }
    set itemssource(value) {
        this._itemssource = value;
        const list = value.split("~");
        this.updateListItems(list);

        this.listName.textContent = this.selecteditem || list[0];
        if (!this.selecteditem) {
            this.SetParameter("selecteditem", this.listName.textContent, true);
        }
    }
    // Использовать входящую связь только для отображения
    get notsendbackwardconnection() {
        return this._lockLink;
    }
    set notsendbackwardconnection(value) {
        this._lockLink = this._toBool(value);
    }
    // Текущий элемент (Выбор)
    get selecteditem() {
        return this._selecteditem;
    }
    set selecteditem(value) {
        this._selecteditem = value;
        const list = (this._itemssource ?? "").split("~");
        const index = list.findIndex(o => o == value);
        this.listName.textContent = index > 0 ? value : list[0];
        if (value === "" && list.length) {
            this.SetParameter("selecteditem", this.listName.textContent, true);
        }
    }

    // Изображение фона
    get resource() {
        return this._resource;
    }
    set resource(value) {
        if (value) {
            this._resource = this.getResourceFromList(value);
            this._calcBacgraund(this.combobox);
            this._calcBlockShadow();
            this.backgroundcolor = "transparent";
        } else {
            this.combobox.style.backgroundImage = "";
            this.backgroundcolor = this.getAttribute("backgroundcolor") ?? "rgb(245,240,245)";
        }
    }

    updateListItems(newList) {
        const currentList = this.listContent.querySelectorAll(".combobox-list__item");
        newList.forEach((x, i) => {
            currentList[i]
                ? this.updateItemContent(currentList[i], x)
                : this.listContent.appendChild(this.createListItem(x));
        })
        if (newList.length < currentList.length) {
            [...currentList].slice(newList.length).forEach(x => x.remove());
        }
        this.listName.title = newList[0];
    }

    updateItemContent(currentItem, newItem) {
        const itemTextElement = currentItem.querySelector('.combobox-list__item-text');
        if (itemTextElement.textContent !== newItem) {
            itemTextElement.textContent = newItem;
            itemTextElement.title = newItem;
        }
    }

    createListItem(item) {
        const li = document.createElement("li");
        li.classList.add("combobox-list__item");
        const span = document.createElement("span");
        span.classList.add("combobox-list__item-text");
        span.textContent = item;
        span.title = item;
        li.appendChild(span);
        li.addEventListener("mouseover", (event) => this.handleMouseOver(event));
        li.addEventListener("mouseleave", (event) => this.handleMouseLeave(event));
        li.addEventListener("click", () => this.selectItem(li.textContent));
        return li;
    }

    selectItem(textContent) {
        this.listName.textContent = textContent
        this.listName.title = textContent;
        this.setListItem(textContent);
        this.comboboxBtn.click()
    }

    setListItem(value) {
        // ignorePerm - при загрузке страницы не нужно отображать модалки
        this.SetParameter("selecteditem", value, false, null, null, "", {
            beforeCallbackFn: () => {
                this._interaction = true;
            },
            afterCallbackFn: () => {
                this._interaction = false;
            },
        })
    }

    setListVisibility(isVisible) {
        if (!document.body.contains(this.listWrapper)) {
            document.body.appendChild(this.listWrapper)
        }
        this.listWrapper.style.display = isVisible ? 'block' : 'none';
        this.comboboxBtn.classList.toggle('rotated', isVisible);
    }

    maxItems() {
        const items = this.listContent.querySelectorAll(".combobox-list__item");
        const maxVisibleItems = 10;
        let totalHeight = 0;
        items.forEach(item => {
            totalHeight += item.offsetHeight;
        });

        const { bottom , top } = this.getBoundingClientRect();
        const spaceBelow = window.innerHeight - bottom;
        const spaceAbove = top;
        const availableSpace = Math.max(spaceBelow , spaceAbove)
    
        if (items.length <= maxVisibleItems && totalHeight <= availableSpace) {
            this.listContent.style.overflow = 'hidden';
            this.listContent.style.maxHeight = `${totalHeight}px`;
            this.listWrapper.style.maxHeight = `${totalHeight}px`;
        } else {
            const height = Math.min(totalHeight, availableSpace);
            this.listContent.style.overflow = 'auto';
            this.listContent.style.maxHeight = `${height - 5}px`;
            this.listWrapper.style.maxHeight = `${height - 5}px`;
        }
    }

    toggleDropdown() {
        if (!this.isenabled)
            return;
        if ($pm.checkEnable(this)) {
            if (!this.listContent.hasChildNodes()) {
                const list = (this._itemssource ?? "").split("~")   
                this.updateListItems(list)
            }
            this.listWrapper.style.width = `${this.comboboxBtn.offsetWidth}px`
            this.setListVisibility(this.listWrapper.style.display !== 'block');
            this.maxItems()
            this.updateDropdown()
        }
        else if (!_globalParams.BlockDisabledControls) {
            $pm.showErrorIfDisabled(this, "Control");
        }
    }

    updateDropdown() {
        const { left, width } = this.comboboxBtn.getBoundingClientRect();
        const x = left;
        this.listContent.style.minWidth = `${width}px`
        this.listWrapper.style.left = `${x}px`;
        this.listWrapper.style.width = `${this.listContent.offsetWidth}px`;
        this.listContent.style.maxWidth = `${window.innerWidth - x - 3}px`
        this.adjustListHeight();
    }

    adjustListHeight() {
        const { bottom, top } = this.getBoundingClientRect();
        const itemHeight = this.listContent.querySelector('li').offsetHeight;
        const spaceBelow = document.body.getBoundingClientRect().bottom - bottom;
        const spaceAbove = top;
        const listStyle = this.listWrapper.style;

        if (this.listWrapper.offsetHeight > spaceAbove && this.listWrapper.offsetHeight > spaceBelow) {
            if (spaceAbove > spaceBelow) {
                listStyle.bottom = `${window.innerHeight - top}px`;
                listStyle.top = 'auto';
                const newMaxHeightAbove = Math.floor(spaceAbove / itemHeight) * itemHeight;
                listStyle.maxHeight = `${newMaxHeightAbove}px`;
            } else {
                listStyle.top = `${bottom}px`;
                listStyle.bottom = "auto"
                const newMaxHeightBelow = Math.floor(spaceBelow / itemHeight) * itemHeight;
                listStyle.maxHeight = `${newMaxHeightBelow}px`
            }
        } else {
            if (this.listWrapper.offsetHeight > spaceBelow) {
                listStyle.bottom = `${window.innerHeight - top}px`;
                listStyle.top = 'auto';
            } else {
                listStyle.top = `${bottom}px`;
                listStyle.bottom = "auto";
            }
        }
    }

    moveFocus(step) {
        if (this.keyboardFocusDisabled) return;
        const items = [...this.listContent.querySelectorAll('.combobox-list__item')];
        const focusedIndex = items.findIndex((item) => item.classList.contains('focused'));
        if (focusedIndex >= 0) items[focusedIndex].classList.remove('focused');
        const newIndex = (focusedIndex + step + items.length) % items.length;
        items[newIndex].classList.add('focused');
        items[newIndex].scrollIntoView({ block: 'nearest' });
    }

    selectFocusedItem() {
        const focusedItem = this.listContent.querySelector('.combobox-list__item.focused');
        if (focusedItem) this.selectItem(focusedItem.textContent);
        this.setListVisibility(false);
    }

    manageFocus(event, addFocus) {
        const items = [...this.listContent.querySelectorAll('.combobox-list__item')];
        items.forEach((item) => item.classList.remove('focused'));
        if (addFocus) {
            event.target.classList.add('focused');
        }
    }
    
    handleMouseOver(event) {
        this.keyboardFocusDisabled = true;
        this.manageFocus(event, true);
    }
    
    handleMouseLeave(event) {
        this.keyboardFocusDisabled = false;
        event.target.classList.remove('focused');
    }

    connectedCallback() {
        this.handleDocumentClick = ({ target }) => {
            if (target === this) {
                this.toggleDropdown()
            } else {
                if (this.listWrapper.style.display === 'block') {
                    this.setListVisibility(false)
                }
            }
        };

        this.resizeListener = () => {
            if (this.listWrapper.style.display === 'block') {
                this.setListVisibility(false)
            }
        }

        this.handleKeyDown = (event) => {
            if (event.key === 'Enter') {
                this.listWrapper.style.display === 'block' ? this.selectFocusedItem() : this.toggleDropdown();
            } else if (this.listWrapper.style.display === 'block') {
                if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    this.moveFocus(1);
                } else if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    this.moveFocus(-1);
                } else if (event.key === 'Tab') {
                    this.setListVisibility(false)
                } else if (event.key === 'Escape') {
                    this.setListVisibility(false)
                }
            }
        };
        this.addEventListener("keydown", this.handleKeyDown)
        document.addEventListener("click", this.handleDocumentClick);
        window.addEventListener("resize", this.resizeListener);
    }

    disconnectedCallback() {
        if (this.listWrapper?.style.display === 'block') {
            this.setListVisibility(false)
        }
        document.removeEventListener("click", this.handleDocumentClick);
        window.removeEventListener("resize", this.resizeListener);
        this.removeEventListener("keydown", this.handleKeyDown);
        super.disconnectedCallback();
    }

    /**
     * Коллбек по изменению атрибутов элемента
     * @see https://w3c.github.io/webcomponents/spec/custom/
     * @param {string} attrName название атрибута
     * @param {*} oldVal старое значение атрибута
     * @param {*} newVal новое значение атрибута
     */
    attributeChangedCallback(attrName, oldVal, newVal) {
        if (oldVal !== newVal) {
            if (attrName === "selecteditem" && !this._interaction) {
                if (this._lockLink) {
                    this[attrName] = newVal;
                    return;
                }
            }
            if (oldVal != newVal) {
                this[attrName] = newVal;
                if (this.prop_link[attrName]) {
                    this.prop_link[attrName](
                        this.pId ? this.pId + "/" + this.id : this.id,
                        this.Links[attrName],
                        attrName === "selecteditem" ? this[attrName] : newVal, // значение может измениться
                        attrName,
                        this.ItemIndex,
                        null,
                        this._interaction
                    );
                }
            }
        }
    }
}